import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiTarget, FiUsers, FiAward, FiTrendingUp, FiMapPin, FiGlobe } = FiIcons;

const About = () => {
  const values = [
    {
      icon: FiTarget,
      title: 'Results-Driven',
      description: 'We deliver measurable results with 70% cost reduction and 3x efficiency improvements for Australian businesses'
    },
    {
      icon: FiUsers,
      title: 'Australia-Wide Service',
      description: 'Serving businesses across Sydney, Melbourne, Brisbane, Perth, Adelaide and regional Australia'
    },
    {
      icon: FiAward,
      title: 'Proven Excellence',
      description: 'Trusted by 500+ Australian businesses with 99% satisfaction rate and industry-leading automation solutions'
    },
    {
      icon: FiTrendingUp,
      title: 'Scalable Growth',
      description: 'Our automation solutions grow with your business, ensuring sustainable long-term success and ROI'
    }
  ];

  return (
    <section id="about" className="py-12 md:py-16 lg:py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-8 md:gap-16 items-center">
          {/* Left Column - Image */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="relative"
          >
            <img
              src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2071&q=80"
              alt="Professional Australian business team collaborating on automation solutions"
              className="rounded-2xl shadow-xl w-full h-auto object-cover aspect-[4/3] md:aspect-auto"
            />
            <div className="absolute inset-0 bg-gradient-to-br from-primary-600/10 to-accent-500/10 rounded-2xl"></div>
            
            {/* Australia Badge */}
            <div className="absolute top-4 left-4 bg-white rounded-lg p-2 md:p-3 shadow-lg">
              <div className="flex items-center space-x-1 md:space-x-2">
                <SafeIcon icon={FiMapPin} className="h-4 w-4 md:h-5 md:w-5 text-primary-600" />
                <span className="text-xs md:text-sm font-semibold text-gray-900">Australia Wide</span>
              </div>
            </div>
          </motion.div>
          
          {/* Right Column - Content */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <div className="mb-6 md:mb-8">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 md:mb-6">
                Australia's Leading <span className="text-primary-600">Automation Experts</span>
              </h2>
              <div className="w-16 md:w-20 h-1 bg-primary-600 mb-4 md:mb-6"></div>
              
              {/* Vision Statement */}
              <div className="bg-primary-50 p-4 md:p-6 rounded-xl mb-4 md:mb-6 border-l-4 border-primary-600">
                <h3 className="text-lg md:text-xl font-semibold text-primary-800 mb-2">🚀 Our Mission</h3>
                <p className="text-base md:text-lg text-primary-700 leading-relaxed">
                  <strong>Help Australian businesses save 70% costs and achieve 3x efficiency</strong> through intelligent automation solutions.
                </p>
              </div>
              
              <p className="text-base md:text-lg text-gray-600 mb-6 md:mb-8 leading-relaxed">
                We're Australia's trusted business process automation specialists, serving companies across Sydney, Melbourne, Brisbane, Perth, Adelaide and beyond. Our proven solutions eliminate manual work, reduce errors, and scale your operations effortlessly.
              </p>
              
              {/* Australian Coverage */}
              <div className="bg-blue-50 p-4 md:p-6 rounded-xl mb-6 md:mb-8 border-l-4 border-blue-500">
                <div className="flex items-center mb-2 md:mb-3">
                  <SafeIcon icon={FiGlobe} className="h-5 w-5 md:h-6 md:w-6 text-blue-600 mr-2 md:mr-3" />
                  <h3 className="text-base md:text-lg font-semibold text-blue-800">Nationwide Coverage</h3>
                </div>
                <p className="text-sm md:text-base text-blue-700">
                  Serving businesses across all major Australian cities and regional areas with local support and understanding of Australian business needs.
                </p>
              </div>
            </div>
            
            {/* Values Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6">
              {values.map((value, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-white p-4 md:p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow"
                >
                  <div className="flex items-center mb-3 md:mb-4">
                    <div className="bg-primary-100 p-2 md:p-3 rounded-lg mr-3 md:mr-4">
                      <SafeIcon icon={value.icon} className="h-5 w-5 md:h-6 md:w-6 text-primary-600" />
                    </div>
                    <h3 className="text-base md:text-lg font-semibold text-gray-900">{value.title}</h3>
                  </div>
                  <p className="text-sm md:text-base text-gray-600 leading-relaxed">{value.description}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default About;